import React from "react";
import { Activity, Thermometer } from "lucide-react";
import { formatSensorType } from "../../../../shared/utils/formatters";
import { buildFrequencyRows, buildTemperatureRows } from "../utils/buildFrequencyTemperatureRows";
import { FrequencyTemperatureTable } from "../FrequencyTemperatureTable";

interface PreviewStepProps {
  individualCalibrationData: any;
  calibrationFieldConfig: any;
  schemaConfig: {
    calibrationPoints: any;
    generatePlaceholder: (point: number, sequence?: "inc" | "dec" | "inc2") => string;
  };
}

/**
 * Read-only summary of everything entered in steps 1 and 2, shown before the
 * actual submit. Reuses FrequencyTemperatureTable in readOnly mode for the
 * frequency/temperature values, so this can never drift out of sync with
 * what Step 2 actually collects.
 */
export const PreviewStep: React.FC<PreviewStepProps> = ({
  individualCalibrationData,
  calibrationFieldConfig,
  schemaConfig,
}) => {
  const frequencyRows = buildFrequencyRows(schemaConfig);
  const temperatureRows = buildTemperatureRows();

  const sensorInfoRows: { label: string; value: string }[] = [
    { label: "Serial Number", value: individualCalibrationData.sensorId },
    { label: "Sensor Type", value: formatSensorType(individualCalibrationData.sensorType) },
    { label: "Category", value: individualCalibrationData.category },
  ];
  if (individualCalibrationData.modelFamily) {
    sensorInfoRows.push({ label: "Displacement Sensor Type", value: individualCalibrationData.modelFamily });
  }
  sensorInfoRows.push(
    { label: "Model", value: individualCalibrationData.model },
    { label: "Range", value: individualCalibrationData.range }
  );

  const additionalFieldRows: { label: string; value: string }[] =
    calibrationFieldConfig?.additionalFields
      ?.filter((field: any) => !field.condition || field.condition(individualCalibrationData))
      .map((field: any) => ({
        label: field.label,
        value: individualCalibrationData[field.key] || "—",
      })) ?? [];

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200">
        <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
          <h4 className="text-sm font-semibold text-gray-800">Sensor Information</h4>
        </div>
        <table className="w-full">
          <tbody>
            {sensorInfoRows.concat(additionalFieldRows).map((row) => (
              <tr key={row.label} className="border-t border-gray-100">
                <td className="px-4 py-2 text-sm text-gray-700">{row.label}</td>
                <td className="px-4 py-2 text-sm font-medium text-gray-900">{row.value || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <FrequencyTemperatureTable
          readOnly
          icon={<Activity className="h-4 w-4 text-blue-600" />}
          title="Frequency Data"
          subtitle="Values entered in the previous step"
          headerLabel="Frequency"
          headerAccent="bg-blue-50"
          rows={frequencyRows}
          individualCalibrationData={individualCalibrationData}
        />
        <FrequencyTemperatureTable
          readOnly
          icon={<Thermometer className="h-4 w-4 text-red-500" />}
          title="Temperature Data"
          subtitle="Values entered in the previous step"
          headerLabel="Temperature Point"
          headerAccent="bg-red-50"
          rows={temperatureRows}
          individualCalibrationData={individualCalibrationData}
        />
      </div>

      <p className="text-sm text-gray-500">
        Review the values above. Click "Process Data" to submit this calibration, or "Back" to make changes.
      </p>
    </div>
  );
};
